import { useState, useMemo, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  PlusIcon,
  ArrowPathIcon,
  Cog6ToothIcon,
  ExclamationTriangleIcon,
  ServerStackIcon,
} from '@heroicons/react/24/outline';
import Card, { CardBody } from '@/components/ui/Card';
import Button from '@/components/ui/Button';
import Badge from '@/components/ui/Badge';
import Spinner from '@/components/ui/Spinner';
import EmptyState from '@/components/ui/EmptyState';
import { useApi } from '@/hooks/useApi';
import { apiClient } from '@/lib/api';
import type {
  DistributorsListResponse,
  DistributorsPublishedCountsResponse,
  Distributor,
  ConnectorJobsListResponse,
} from '@/types/api';
import AddPlatformModal from './AddPlatformModal';

// Icon mapping for known connector types
const connectorIcons: Record<string, string> = {
  'apple-books': '🍎',
  'google-books': '▶️',
  'spotify': '🎵',
  'sftp': '📁',
  'ftp': '📂',
  'api': '🔌',
  'webhook': '🔔',
};

function getConnectorIcon(connectorType?: string): string {
  const ct = (connectorType || '').trim().toLowerCase();
  if (!ct) return '📦';
  return connectorIcons[ct] || '📦';
}

function getDistributorDisplayName(d: { distributor_name?: string; name?: string; code?: string }): string {
  return d.distributor_name || d.name || d.code || '—';
}

export default function DistPlatformsPage() {
  const [showAddModal, setShowAddModal] = useState(false);
  const [jobCountByDistributorId, setJobCountByDistributorId] = useState<Record<string, number>>({});
  const [isLoadingJobCounts, setIsLoadingJobCounts] = useState(false);

  // Build API endpoint with query params
  const apiEndpoint = useMemo(() => {
    const params = new URLSearchParams();
    params.set('pageSize', '50');
    const queryString = params.toString();
    return `distribution/platforms${queryString ? `?${queryString}` : ''}`;
  }, []);

  // Fetch distributors from API
  const { data, isLoading, error, refetch } = useApi<DistributorsListResponse>(apiEndpoint);
  const {
    data: publishedCounts,
    isLoading: isLoadingPublishedCounts,
    error: publishedCountsError,
    refetch: refetchPublishedCounts,
  } = useApi<DistributorsPublishedCountsResponse>('distribution/platforms/published-counts');

  const distributors = data?.items || [];
  const { countById, countByCode, countByName } = useMemo(() => {
    const byId: Record<string, number> = {};
    const byCode: Record<string, number> = {};
    const byName: Record<string, number> = {};

    // Handle shape variations returned by different backend versions.
    (publishedCounts || []).forEach((rawRow) => {
      const row = rawRow as typeof rawRow & {
        id?: string;
        distributorId?: string;
        code?: string;
        distributor_code?: string;
        name?: string;
      };

      const publishedCount = row.published_count ?? 0;
      const distributorId = row.distributor_id || row.id || row.distributorId;
      const distributorCode = row.distributor_code || row.code;
      const distributorName = row.distributor_name || row.name;

      if (distributorId) byId[distributorId] = publishedCount;
      if (distributorCode) byCode[distributorCode] = publishedCount;
      if (distributorName) byName[distributorName] = publishedCount;
    });

    return {
      countById: byId,
      countByCode: byCode,
      countByName: byName,
    };
  }, [publishedCounts]);

  useEffect(() => {
    let cancelled = false;

    async function fetchSucceededJobTotals() {
      if (!distributors.length) {
        if (!cancelled) setJobCountByDistributorId({});
        return;
      }

      setIsLoadingJobCounts(true);
      try {
        const results = await Promise.all(
          distributors.map(async (distributor) => {
            const params = new URLSearchParams({
              distributor_id: distributor.id,
              status: 'succeeded',
              page: '1',
              pageSize: '1',
            });

            const response = await apiClient.get<ConnectorJobsListResponse>(`admin/connector-jobs?${params.toString()}`);
            return [distributor.id, response.total || 0] as const;
          })
        );

        if (!cancelled) {
          const next: Record<string, number> = {};
          results.forEach(([id, total]) => {
            next[id] = total;
          });
          setJobCountByDistributorId(next);
        }
      } catch {
        // Keep existing non-job fallbacks when this optional aggregate fails.
      } finally {
        if (!cancelled) setIsLoadingJobCounts(false);
      }
    }

    fetchSucceededJobTotals();

    return () => {
      cancelled = true;
    };
  }, [distributors]);

  const getBooksSentCount = (distributor: Distributor): number => {
    return (
      jobCountByDistributorId[distributor.id] ??
      countById[distributor.id] ??
      countByCode[distributor.code] ??
      countByName[getDistributorDisplayName(distributor)] ??
      distributor.books_sent ??
      distributor.published_count ??
      distributor.books_count ??
      0
    );
  };

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Page Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Distribution Platforms</h1>
          <p className="text-slate-500 mt-1">Configure distribution connectors</p>
        </div>
        <div className="flex gap-3">
          <Button
            variant="outline"
            leftIcon={<ArrowPathIcon className="w-4 h-4" />}
            onClick={() => {
              refetch();
              refetchPublishedCounts();
            }}
            disabled={isLoading}
          >
            Refresh
          </Button>
          <Button leftIcon={<PlusIcon className="w-4 h-4" />} onClick={() => setShowAddModal(true)}>
            Add Platform
          </Button>
        </div>
      </div>

      {/* Error State */}
      {error && (
        <Card padding="lg">
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <ExclamationTriangleIcon className="w-12 h-12 text-danger-500 mb-4" />
            <h3 className="text-lg font-semibold text-slate-800 mb-2">Failed to load platforms</h3>
            <p className="text-slate-500 mb-4">{error}</p>
            <Button onClick={() => refetch()} leftIcon={<ArrowPathIcon className="w-4 h-4" />}>
              Try Again
            </Button>
          </div>
        </Card>
      )}

      {/* Loading State */}
      {isLoading && !data && (
        <Card padding="lg">
          <div className="flex flex-col items-center justify-center py-12">
            <Spinner size="lg" />
            <p className="text-slate-500 mt-4">Loading platforms...</p>
          </div>
        </Card>
      )}

      {/* Published Counts Error (non-blocking) */}
      {!error && publishedCountsError && (
        <Card padding="sm">
          <div className="text-sm text-slate-600">
            Unable to load “Books Sent” counts. Please refresh.
          </div>
        </Card>
      )}

      {/* Empty State */}
      {!isLoading && !error && distributors.length === 0 && (
        <EmptyState
          icon={<ServerStackIcon className="w-12 h-12" />}
          title="No platforms found"
          description="Get started by adding your first distribution platform"
          action={
            <Button
              leftIcon={<PlusIcon className="w-4 h-4" />}
              onClick={() => setShowAddModal(true)}
            >
              Add Platform
            </Button>
          }
        />
      )}

      {/* Platforms Grid */}
      {!error && distributors.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {distributors.map((distributor) => (
            <Card key={distributor.id} hover>
              <CardBody className="text-center">
                <div className="text-5xl mb-4">{getConnectorIcon(distributor.connector_type)}</div>
                <h3 className="text-lg font-semibold text-slate-800">{getDistributorDisplayName(distributor)}</h3>
                <p className="text-sm text-slate-500 mt-1">Connector: {distributor.connector_type || '—'}</p>
                <p className="text-sm text-slate-500">Mode: {distributor.mode || '—'}</p>
                <div className="mt-4">
                  <Badge variant={distributor.active !== false ? 'success' : 'warning'}>
                    {distributor.active !== false ? '✅ Active' : '⚙️ Config Required'}
                  </Badge>
                </div>
                <p className="text-2xl font-bold text-slate-800 mt-4">
                  {(isLoadingPublishedCounts && !publishedCounts) || (isLoadingJobCounts && !(distributor.id in jobCountByDistributorId))
                    ? '—'
                    : getBooksSentCount(distributor)}
                </p>
                <p className="text-xs text-slate-500">Books Sent</p>
                <Link to={`/distribution/platforms/${distributor.id}`}>
                  <Button variant="outline" className="mt-4 w-full" leftIcon={<Cog6ToothIcon className="w-4 h-4" />}>
                    Configure
                  </Button>
                </Link>
              </CardBody>
            </Card>
          ))}
        </div>
      )}

      {/* Add Platform Modal */}
      <AddPlatformModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSuccess={() => refetch()}
      />
    </div>
  );
}
